import type { ReactNode } from "react";

/**
 * Model output rendered as STRUCTURE, never as markup (W8-11).
 *
 * kms injected message bodies with `dangerouslySetInnerHTML` after replacing `\n` with
 * `<br/>`, which was both an XSS surface and the reason a model's `##` heading rendered as
 * literal hashes. Nothing here ever produces an HTML string: the text is parsed into React
 * elements, so there is no path from a model's answer to the DOM as markup.
 *
 * Headings, fenced code, lists, blockquotes, inline code and bold earn their place because
 * each carries meaning a `<pre>` would lose. Everything else renders as the literal text it is.
 */

const BOLD = /(\*\*[^*]+\*\*)/g;
const CODE = /(`[^`]+`)/g;

function inline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  let offset = 0;
  for (const chunk of text.split(CODE)) {
    const at = offset;
    offset += chunk.length;
    if (chunk.startsWith("`") && chunk.endsWith("`") && chunk.length > 2) {
      out.push(
        <code
          key={`${keyPrefix}-c-${at}`}
          dir="ltr"
          className="rounded-sm bg-bg-secondary px-1 font-mono text-small"
        >
          {chunk.slice(1, -1)}
        </code>,
      );
      continue;
    }
    let inner = 0;
    for (const part of chunk.split(BOLD)) {
      const innerAt = at + inner;
      inner += part.length;
      if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
        out.push(
          <strong key={`${keyPrefix}-b-${innerAt}`} className="font-semibold">
            {part.slice(2, -2)}
          </strong>,
        );
      } else if (part.length > 0) {
        out.push(<span key={`${keyPrefix}-t-${innerAt}`}>{part}</span>);
      }
    }
  }
  return out;
}

const HEADING_CLASS: Record<number, string> = {
  1: "text-body font-semibold text-text",
  2: "text-body font-semibold text-text",
  3: "text-small font-semibold text-text",
};

export function MessageMarkdown({ content }: { content: string }) {
  const blocks: ReactNode[] = [];
  const lines = content.split("\n");
  let index = 0;
  let key = 0;

  while (index < lines.length) {
    const line = lines[index] ?? "";

    if (line.trimStart().startsWith("```")) {
      const code: string[] = [];
      const fence = line.trimStart().slice(3).trim();
      index += 1;
      while (index < lines.length && !(lines[index] ?? "").trimStart().startsWith("```")) {
        code.push(lines[index] ?? "");
        index += 1;
      }
      index += 1;
      key += 1;
      blocks.push(
        <pre
          key={`code-${key}`}
          dir="ltr"
          data-language={fence || undefined}
          className="overflow-x-auto rounded-sm bg-bg-secondary p-2 text-start font-mono text-small text-text-secondary"
        >
          {code.join("\n")}
        </pre>,
      );
      continue;
    }

    const heading = /^(#{1,3})\s+(.*)$/.exec(line.trim());
    if (heading) {
      key += 1;
      const level = (heading[1] ?? "#").length;
      blocks.push(
        <p key={`h-${key}`} className={HEADING_CLASS[level] ?? HEADING_CLASS[3]}>
          {inline(heading[2] ?? "", `h${key}`)}
        </p>,
      );
      index += 1;
      continue;
    }

    if (/^\s*>\s?/.test(line)) {
      const quote: string[] = [];
      while (index < lines.length && /^\s*>\s?/.test(lines[index] ?? "")) {
        quote.push((lines[index] ?? "").replace(/^\s*>\s?/, ""));
        index += 1;
      }
      key += 1;
      blocks.push(
        <blockquote
          key={`quote-${key}`}
          className="border-s-2 border-divider ps-2 text-text-secondary"
        >
          {inline(quote.join("\n"), `q${key}`)}
        </blockquote>,
      );
      continue;
    }

    if (/^\s*(?:[-*]|\d+\.)\s+/.test(line)) {
      const ordered = /^\s*\d+\.\s+/.test(line);
      const items: string[] = [];
      while (index < lines.length && /^\s*(?:[-*]|\d+\.)\s+/.test(lines[index] ?? "")) {
        items.push((lines[index] ?? "").replace(/^\s*(?:[-*]|\d+\.)\s+/, ""));
        index += 1;
      }
      key += 1;
      const listKey = key;
      /*
       * Keyed by the item's OFFSET within the list, not its array index: two identical items
       * stay distinct and the key changes when the text does.
       */
      let cursor = 0;
      const children = items.map((item) => {
        const at = cursor;
        cursor += item.length;
        return (
          <li key={`list-${listKey}-${at}-${item.slice(0, 12)}`}>
            {inline(item, `l${listKey}-${at}`)}
          </li>
        );
      });
      blocks.push(
        ordered ? (
          <ol key={`list-${listKey}`} className="list-decimal ps-5">
            {children}
          </ol>
        ) : (
          <ul key={`list-${listKey}`} className="list-disc ps-5">
            {children}
          </ul>
        ),
      );
      continue;
    }

    if (line.trim() === "") {
      index += 1;
      continue;
    }

    const paragraph: string[] = [];
    while (
      index < lines.length &&
      (lines[index] ?? "").trim() !== "" &&
      !/^\s*(?:[-*]|\d+\.)\s+/.test(lines[index] ?? "") &&
      !/^\s*>\s?/.test(lines[index] ?? "") &&
      !/^#{1,3}\s+/.test((lines[index] ?? "").trim()) &&
      !(lines[index] ?? "").trimStart().startsWith("```")
    ) {
      paragraph.push(lines[index] ?? "");
      index += 1;
    }
    key += 1;
    blocks.push(<p key={`para-${key}`}>{inline(paragraph.join("\n"), `p${key}`)}</p>);
  }

  return (
    <div className="flex flex-col gap-2 whitespace-pre-wrap break-words text-start">{blocks}</div>
  );
}
